<script lang="js">
import axios from "axios";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "Settings",
  data() {
    return {
      logger: null,
      activeTab: "profile",
      isChangingPassword: false,
      passwordForm: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      passwordErrors: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    };
  },
  created() {
    this.logger = this.$createLogger("AppSettings");
  },
  computed: {
    ...mapGetters(["getUser", "getApplication", "getStudent"]),
    user() {
      return this.getUser || {};
    },
    userData() {
      return {
        ...this.user,
        firstname: this.user.firstName,
        othername: this.user.otherName,
        lastname: this.user.lastName,
        applicationNumber: this.getApplication?.applicationNumber,
        matricNumber: this.getStudent?.matriculationNumber,
      };
    },
    passwordStrength() {
      const password = this.passwordForm.newPassword;
      if (!password) return { strength: 0, label: "", color: "" };

      let strength = 0;
      if (password.length >= 8) strength += 25;
      if (password.length >= 12) strength += 25;
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
      if (/\d/.test(password)) strength += 15;
      if (/[@$!%*?&#]/.test(password)) strength += 10;

      if (strength < 40) return { strength, label: "Weak", color: "danger" };
      if (strength < 70) return { strength, label: "Fair", color: "warning" };
      if (strength < 90) return { strength, label: "Good", color: "info" };
      return { strength: 100, label: "Strong", color: "success" };
    },
  },
  mounted() {
    this.logger.info("AppSettings mounted");
  },
  methods: {
    setActiveTab(tab) {
      this.activeTab = tab;
      this.resetPasswordForm();
    },
    resetPasswordForm() {
      this.passwordForm = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.passwordErrors = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
    },
    validatePasswordForm() {
      let isValid = true;
      this.passwordErrors = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };

      if (!this.passwordForm.currentPassword) {
        this.passwordErrors.currentPassword = "Current password is required";
        isValid = false;
      }

      if (!this.passwordForm.newPassword) {
        this.passwordErrors.newPassword = "New password is required";
        isValid = false;
      } else if (this.passwordForm.newPassword.length < 8) {
        this.passwordErrors.newPassword = "Password must be at least 8 characters";
        isValid = false;
      }

      if (!this.passwordForm.confirmPassword) {
        this.passwordErrors.confirmPassword = "Please confirm your password";
        isValid = false;
      } else if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordErrors.confirmPassword = "Passwords do not match";
        isValid = false;
      }

      if (this.passwordForm.currentPassword === this.passwordForm.newPassword) {
        this.passwordErrors.newPassword = "New password must be different from current password";
        isValid = false;
      }

      return isValid;
    },
    async changePassword() {
      if (!this.validatePasswordForm()) {
        return;
      }

      try {
        this.isChangingPassword = true;

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/auth/change-password`,
          {
            currentPassword: this.passwordForm.currentPassword,
            password: this.passwordForm.newPassword,
            confirmPassword: this.passwordForm.confirmPassword,
          }
        );

        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Password Changed",
            text: "Your password has been updated successfully.",
            timer: 3000,
            showConfirmButton: false,
          });

          this.resetPasswordForm();
        }
      } catch (error) {
        this.logger.error("Error changing password:", error);

        const errorMessage = error.response?.data?.message || "Failed to change password. Please try again.";

        Swal.fire({
          icon: "error",
          title: "Password Change Failed",
          text: errorMessage,
        });
      } finally {
        this.isChangingPassword = false;
      }
    },
  },
  components: {},
};
</script>

<template>
  <div class="p-4 text-start">
    <h4 class="fw-bold">Settings</h4>
    <hr />

    <!-- Tabs Navigation -->
    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item" role="presentation">
        <button
          :class="['nav-link', { active: activeTab === 'profile' }]"
          @click="setActiveTab('profile')"
          type="button"
          role="tab"
        >
          <i class="bi bi-person me-2"></i>Profile
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          :class="['nav-link', { active: activeTab === 'preferences' }]"
          @click="setActiveTab('preferences')"
          type="button"
          role="tab"
        >
          <i class="bi bi-sliders me-2"></i>Preferences
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          :class="['nav-link', { active: activeTab === 'security' }]"
          @click="setActiveTab('security')"
          type="button"
          role="tab"
        >
          <i class="bi bi-shield-lock me-2"></i>Security
        </button>
      </li>
    </ul>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Profile Tab -->
      <div
        v-show="activeTab === 'profile'"
        class="tab-pane fade"
        :class="{ 'show active': activeTab === 'profile' }"
        role="tabpanel"
      >
        <div class="row">
          <div class="col-lg-12 mx-auto">
            <!-- Basic Information Section -->
            <h6 class="mb-2">Basic Information</h6>
            <div class="card border-0 mb-4">
              <!-- <div class="card-header border-bottom-0 bg-white">
                <h6 class="mb-0 fw-bold">Basic Information</h6>
                <small class="text-muted">Your personal details</small>
              </div> -->
              <div class="card-body">
                <div class="row mb-3">
                  <div class="col-md-4">
                    <label class="form-label fw-semibold"
                      >Application Number</label
                    >
                    <p
                      class="form-control-plaintext border rounded px-3 py-2 bg-light"
                    >
                      {{ userData.applicationNumber || "N/A" }}
                    </p>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label fw-semibold">Matric Number</label>
                    <p
                      class="form-control-plaintext border rounded px-3 py-2 bg-light"
                    >
                      {{ userData.matricNumber || "Not assigned" }}
                    </p>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label fw-semibold">Account Status</label>
                    <p class="form-control-plaintext pe-3 py-2">
                      <span
                        :class="[
                          'badge',
                          userData.status === 'active'
                            ? 'bg-success'
                            : 'bg-secondary',
                        ]"
                        class="text-capitalize"
                      >
                        {{ userData.status || "N/A" }}
                      </span>
                    </p>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4">
                    <label class="form-label fw-semibold">First Name</label>
                    <p
                      class="form-control-plaintext border rounded px-3 py-2 bg-light text-capitalize"
                    >
                      {{ userData.firstname || "N/A" }}
                    </p>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label fw-semibold">Other Name</label>
                    <p
                      class="form-control-plaintext border rounded px-3 py-2 bg-light text-capitalize"
                    >
                      {{ userData.othername || "N/A" }}
                    </p>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label fw-semibold">Last Name</label>
                    <p
                      class="form-control-plaintext border rounded px-3 py-2 bg-light text-capitalize"
                    >
                      {{ userData.lastname || "N/A" }}
                    </p>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Email</label>
                    <p
                      class="form-control-plaintext border rounded px-3 py-2 bg-light"
                    >
                      {{ userData.email || "N/A" }}
                    </p>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Phone</label>
                    <p
                      class="form-control-plaintext border rounded px-3 py-2 bg-light"
                    >
                      {{ userData.phone || "N/A" }}
                    </p>
                  </div>
                </div>

                <div
                  class="alert alert-info d-flex align-items-start"
                  role="alert"
                >
                  <i class="bi bi-info-circle me-2 mt-1"></i>
                  <div>
                    <strong>Need to update your information?</strong>
                    <p class="mb-0 small">
                      Please contact the admissions office or your department to
                      request changes to your profile information.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Change Password Section -->
            <h6 class="mb-2">Change Password</h6>
            <div class="card border-0">
              <!-- <div class="card-header border-bottom-0 bg-white">
                <h6 class="mb-0 fw-bold">Change Password</h6>
                <small class="text-muted">Update your account password</small>
              </div> -->
              <div class="card-body">
                <form @submit.prevent="changePassword">
                  <div class="mb-3">
                    <label for="currentPassword" class="form-label fw-bold">
                      Current Password
                      <span class="text-danger fw-normal">*</span>
                    </label>
                    <div class="input-group">
                      <input
                        :type="showCurrentPassword ? 'text' : 'password'"
                        class="form-control border-end-0"
                        :class="{
                          'is-invalid': passwordErrors.currentPassword,
                        }"
                        id="currentPassword"
                        v-model="passwordForm.currentPassword"
                        placeholder="Enter current password"
                      />
                      <button
                        class="btn border-light-subtle border-start-0 rounded-end"
                        type="button"
                        @click="showCurrentPassword = !showCurrentPassword"
                      >
                        <i
                          :class="[
                            'bi',
                            showCurrentPassword ? 'bi-eye-slash' : 'bi-eye',
                          ]"
                        ></i>
                      </button>
                      <div class="invalid-feedback">
                        {{ passwordErrors.currentPassword }}
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="newPassword" class="form-label fw-bold">
                      New Password <span class="text-danger fw-normal">*</span>
                    </label>
                    <div class="input-group">
                      <input
                        :type="showNewPassword ? 'text' : 'password'"
                        class="form-control border-end-0"
                        :class="{ 'is-invalid': passwordErrors.newPassword }"
                        id="newPassword"
                        v-model="passwordForm.newPassword"
                        placeholder="Enter new password (min. 8 characters)"
                      />
                      <button
                        class="btn border-light-subtle border-start-0 rounded-end"
                        type="button"
                        @click="showNewPassword = !showNewPassword"
                      >
                        <i
                          :class="[
                            'bi',
                            showNewPassword ? 'bi-eye-slash' : 'bi-eye',
                          ]"
                        ></i>
                      </button>
                      <div class="invalid-feedback">
                        {{ passwordErrors.newPassword }}
                      </div>
                    </div>
                    <!-- Password Strength Indicator -->
                    <div v-if="passwordForm.newPassword" class="mt-2">
                      <small class="text-muted">Password Strength:</small>
                      <div class="progress" style="height: 6px">
                        <div
                          class="progress-bar"
                          :class="`bg-${passwordStrength.color}`"
                          :style="{ width: `${passwordStrength.strength}%` }"
                        ></div>
                      </div>
                      <small :class="`text-${passwordStrength.color}`">
                        {{ passwordStrength.label }}
                      </small>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="confirmPassword" class="form-label fw-bold">
                      Confirm New Password
                      <span class="text-danger fw-normal">*</span>
                    </label>
                    <div class="input-group">
                      <input
                        :type="showConfirmPassword ? 'text' : 'password'"
                        class="form-control border-end-0"
                        :class="{
                          'is-invalid': passwordErrors.confirmPassword,
                        }"
                        id="confirmPassword"
                        v-model="passwordForm.confirmPassword"
                        placeholder="Confirm new password"
                      />
                      <button
                        class="btn border-light-subtle border-start-0 rounded-end"
                        type="button"
                        @click="showConfirmPassword = !showConfirmPassword"
                      >
                        <i
                          :class="[
                            'bi',
                            showConfirmPassword ? 'bi-eye-slash' : 'bi-eye',
                          ]"
                        ></i>
                      </button>
                      <div class="invalid-feedback">
                        {{ passwordErrors.confirmPassword }}
                      </div>
                    </div>
                  </div>

                  <div
                    class="alert alert-warning d-flex align-items-start small"
                    role="alert"
                  >
                    <i class="bi bi-exclamation-triangle me-2 mt-1"></i>
                    <div>
                      <strong>Password Requirements:</strong>
                      <ul class="mb-0 mt-1">
                        <li>At least 8 characters long</li>
                        <li>
                          Mix of uppercase and lowercase letters recommended
                        </li>
                        <li>
                          Include numbers and special characters for stronger
                          security
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div class="d-flex gap-2">
                    <button
                      type="submit"
                      class="btn btn-primary"
                      :disabled="isChangingPassword"
                    >
                      <span
                        v-if="isChangingPassword"
                        class="spinner-border spinner-border-sm me-2"
                      ></span>
                      {{
                        isChangingPassword ? "Changing..." : "Change Password"
                      }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="resetPasswordForm"
                      :disabled="isChangingPassword"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Preferences Tab -->
      <div
        v-show="activeTab === 'preferences'"
        class="tab-pane fade"
        :class="{ 'show active': activeTab === 'preferences' }"
        role="tabpanel"
      >
        <div class="row">
          <div class="col-lg-12 mx-auto">
            <h6 class="mb-2">Preferences</h6>
            <div
              class="card border-0 position-relative"
              style="min-height: 400px"
            >
              <!-- <div class="card-header bg-white">
                <h6 class="mb-0 fw-bold">Preferences</h6>
                <small class="text-muted">Customize your experience</small>
              </div> -->
              <div class="card-body">
                <!-- Notification Preferences -->
                <div class="mb-4">
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-bell me-2"></i>Notification Preferences
                  </h6>
                  <div class="form-check form-switch mb-2">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="emailNotifications"
                      checked
                    />
                    <label class="form-check-label" for="emailNotifications">
                      Email Notifications
                      <small class="text-muted d-block">
                        Receive updates about courses, payments, and admissions
                      </small>
                    </label>
                  </div>
                  <div class="form-check form-switch mb-2">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="inAppNotifications"
                      checked
                    />
                    <label class="form-check-label" for="inAppNotifications">
                      In-App Notifications
                      <small class="text-muted d-block">
                        Show notifications within the portal
                      </small>
                    </label>
                  </div>
                  <div class="mb-3">
                    <label
                      for="notificationFrequency"
                      class="form-label small fw-semibold"
                    >
                      Notification Frequency
                    </label>
                    <select class="form-select" id="notificationFrequency">
                      <option value="immediate" selected>Immediate</option>
                      <option value="daily">Daily Digest</option>
                      <option value="weekly">Weekly Summary</option>
                    </select>
                  </div>
                </div>

                <hr />

                <!-- Display Settings -->
                <div class="mb-4">
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-palette me-2"></i>Display Settings
                  </h6>
                  <div class="form-check form-switch mb-2">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="darkMode"
                    />
                    <label class="form-check-label" for="darkMode">
                      Dark Mode
                      <small class="text-muted d-block">
                        Switch to dark theme for better viewing in low light
                      </small>
                    </label>
                  </div>
                  <div class="mb-3">
                    <label for="language" class="form-label small fw-semibold">
                      Language
                    </label>
                    <select class="form-select" id="language">
                      <option value="en" selected>English</option>
                      <option value="yo">Yoruba</option>
                      <option value="ig">Igbo</option>
                      <option value="ha">Hausa</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Overlay for "Not Available Yet" -->
              <div
                class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-3"
                style="z-index: 10; cursor: not-allowed"
              >
                <div class="text-center text-white">
                  <i class="bi bi-tools fs-1 mb-3"></i>
                  <h5 class="fw-bold">Coming Soon</h5>
                  <p class="mb-0">
                    Preferences customization will be available in a future
                    update
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Security Tab -->
      <div
        v-show="activeTab === 'security'"
        class="tab-pane fade"
        :class="{ 'show active': activeTab === 'security' }"
        role="tabpanel"
      >
        <div class="row">
          <div class="col-lg-12 mx-auto">
            <h6 class="mb-2">Security</h6>
            <div
              class="card border-0 position-relative"
              style="min-height: 400px"
            >
              <!-- <div class="card-header bg-white">
                <h6 class="mb-0 fw-bold">Security</h6>
                <small class="text-muted">Manage your account security</small>
              </div> -->
              <div class="card-body">
                <!-- Login Activity -->
                <div class="mb-4">
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-clock-history me-2"></i>Recent Login
                    Activity
                  </h6>
                  <div class="list-group">
                    <div class="list-group-item">
                      <div
                        class="d-flex justify-content-between align-items-start"
                      >
                        <div>
                          <h6 class="mb-1">Current Session</h6>
                          <p class="mb-1 text-muted small">
                            <i class="bi bi-laptop me-1"></i>Chrome on Windows
                          </p>
                          <p class="mb-0 text-muted small">
                            <i class="bi bi-geo-alt me-1"></i>Lagos, Nigeria •
                            Just now
                          </p>
                        </div>
                        <span class="badge bg-success">Active</span>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                <!-- Two-Factor Authentication -->
                <div class="mb-4">
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-shield-check me-2"></i>Two-Factor
                    Authentication
                  </h6>
                  <div
                    class="alert alert-info d-flex align-items-start"
                    role="alert"
                  >
                    <i class="bi bi-info-circle me-2 mt-1"></i>
                    <div>
                      <strong>Extra Security Layer</strong>
                      <p class="mb-0 small">
                        Add an extra layer of security to your account with
                        two-factor authentication.
                      </p>
                    </div>
                  </div>
                  <button class="btn btn-outline-primary" type="button">
                    <i class="bi bi-shield-plus me-2"></i>Enable 2FA
                  </button>
                </div>

                <hr />

                <!-- Account Status -->
                <div class="mb-4">
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-person-badge me-2"></i>Account Status
                  </h6>
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">Account Status</small>
                      <span
                        :class="[
                          'badge',
                          userData.status === 'active'
                            ? 'bg-success'
                            : 'bg-secondary',
                        ]"
                        class="text-capitalize"
                      >
                        {{ userData.status || "N/A" }}
                      </span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block"
                        >Last Password Change</small
                      >
                      <span class="text-muted">Not available</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Overlay for "Not Available Yet" -->
              <div
                class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-3"
                style="z-index: 10; cursor: not-allowed"
              >
                <div class="text-center text-white">
                  <i class="bi bi-tools fs-1 mb-3"></i>
                  <h5 class="fw-bold">Coming Soon</h5>
                  <p class="mb-0">
                    Security features will be available in a future update
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nav-tabs .nav-link {
  color: #6c757d;
  border: none;
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link:hover {
  border-color: transparent;
  color: #495057;
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  border-bottom-color: #0d6efd;
  background-color: transparent;
}

.form-control-plaintext {
  /* background-color: #f8f9fa; */
  min-height: 38px;
  display: flex;
  align-items: center;
}
</style>
